import { useState, useEffect } from "react";
import {
  Eye,
  Loader2,
  Trash2,
  RefreshCw,
  ExternalLink,
  ArrowRight,
  Clock,
  Search,
  Send,
  Flame,
  Package,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import ExportButton from "../components/ExportButton";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Watchlist() {
  const { email: userEmail } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanningId, setScanningId] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchWatchlist();
  }, []);

  const fetchWatchlist = async () => {
    try {
      const emailParam = userEmail ? `?email=${encodeURIComponent(userEmail)}` : "";
      const [itemsRes, statsRes] = await Promise.all([
        axios.get(`${API}/watchlist${emailParam}`),
        axios.get(`${API}/watchlist/stats`),
      ]);
      setItems(itemsRes.data.watchlist || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching watchlist:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleScanAll = async () => {
    setScanning(true);
    try {
      const response = await axios.post(`${API}/watchlist/scan-all`);
      toast.success(
        `${response.data.scanned} produit(s) scanné(s), ${response.data.alerts_sent} alerte(s) envoyée(s) !`
      );
      fetchWatchlist();
    } catch (error) {
      toast.error("Erreur lors du scan");
    } finally {
      setScanning(false);
    }
  };

  const handleScanOne = async (itemId) => {
    setScanningId(itemId);
    try {
      await axios.post(`${API}/watchlist/scan/${itemId}`);
      toast.success("Produit scanné !");
      fetchWatchlist();
    } catch (error) {
      toast.error("Erreur lors du scan");
    } finally {
      setScanningId(null);
    }
  };

  const handleRemove = async (itemId) => {
    try {
      await axios.delete(`${API}/watchlist/${itemId}`);
      setItems(items.filter((i) => i.id !== itemId));
      toast.success("Retiré de la watchlist");
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "Jamais scanné";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `Il y a ${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Il y a ${hours}h`;
    return `Il y a ${Math.floor(hours / 24)}j`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#00E676]" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="watchlist-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight flex items-center gap-3">
            <Eye className="text-[#00E676]" size={40} />
            WATCHLIST
          </h1>
          <p className="text-muted-foreground mt-2 uppercase tracking-wider text-sm">
            Surveillez vos produits - Alertes automatiques Telegram
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <ExportButton endpoint="watchlist" filename="profimy_watchlist.csv" />
          <button
            onClick={handleScanAll}
            disabled={scanning || items.length === 0}
            className="neo-button-primary flex items-center gap-2"
            data-testid="scan-all-btn"
          >
            {scanning ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <RefreshCw size={18} />
            )}
            Scanner tout
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4" data-testid="watchlist-stats">
          <div className="neo-card p-5">
            <p className="text-3xl font-extrabold">{stats.total_watchlist}</p>
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mt-1">
              Produits suivis
            </p>
          </div>
          <div className="neo-card p-5">
            <p className="text-3xl font-extrabold text-[#00E676]">
              {stats.total_potential_profit.toFixed(0)}€
            </p>
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mt-1">
              Profit potentiel
            </p>
          </div>
          <div className="neo-card p-5">
            <p className="text-3xl font-extrabold">{stats.average_margin}%</p>
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mt-1">
              Marge moyenne
            </p>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="bg-[#0A0A0A] text-white p-4 flex items-center gap-3">
        <Send className="text-[#00E676] flex-shrink-0" size={20} />
        <p className="text-sm">
          Cliquez sur <strong>"Scanner tout"</strong> pour mettre à jour les prix. Si un produit dépasse 30% de marge, une alerte Telegram est envoyée automatiquement à vos abonnés.
        </p>
      </div>

      {/* Items */}
      {items.length > 0 ? (
        <div className="space-y-4" data-testid="watchlist-items">
          {items.map((item) => (
            <div key={item.id} className="neo-card p-5 relative" data-testid={`watchlist-${item.id}`}>
              {/* Margin badge */}
              {item.margin_percent > 0 && (
                <div className={`absolute top-3 right-3 border-2 border-[#0A0A0A] px-3 py-1 font-bold transform rotate-2 text-sm ${
                  item.margin_percent >= 30 ? "bg-[#00E676] text-[#0A0A0A]" : "bg-yellow-400 text-[#0A0A0A]"
                }`}>
                  +{item.margin_percent}%
                </div>
              )}

              <div className="flex gap-4 items-start mb-4 pr-20">
                {item.product_image && (
                  <img src={item.product_image} alt="" className="w-16 h-16 object-contain border border-[#0A0A0A]/20 flex-shrink-0" />
                )}
                <div>
                  <p className="font-bold line-clamp-2">{item.product_name}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <Clock size={12} />
                    <span>{timeAgo(item.last_scanned)}</span>
                    {item.last_opportunities > 0 && (
                      <span className="text-[#00E676]">
                        {item.last_opportunities} opportunité(s)
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Price comparison */}
              {item.buy_price > 0 && item.sell_price > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-xs uppercase text-muted-foreground font-bold">Acheter sur</p>
                    <p className="font-semibold text-sm">{item.buy_platform}</p>
                    <p className="text-xl font-bold">{item.buy_price.toFixed(2)}€</p>
                    {item.buy_url && (
                      <a href={item.buy_url} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-[#00E676] inline-flex items-center gap-1 mt-1">
                        Voir <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                  <div className="flex items-center justify-center">
                    <ArrowRight className="text-[#00E676] hidden sm:block" size={28} />
                  </div>
                  <div>
                    <p className="text-xs uppercase text-muted-foreground font-bold">Vendre sur</p>
                    <p className="font-semibold text-sm">{item.sell_platform}</p>
                    <p className="text-xl font-bold text-[#00E676]">{item.sell_price.toFixed(2)}€</p>
                    {item.sell_url && (
                      <a href={item.sell_url} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-[#00E676] inline-flex items-center gap-1 mt-1">
                        Voir <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {item.margin > 0 && (
                <div className="bg-[#0A0A0A] text-white p-3 flex justify-between items-center mb-4">
                  <span className="font-bold text-sm uppercase">Profit estimé</span>
                  <span className="text-[#00E676] font-bold text-lg">+{item.margin.toFixed(2)}€</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 flex-wrap items-center">
                <button
                  onClick={() => handleScanOne(item.id)}
                  disabled={scanningId === item.id}
                  className="neo-button-secondary text-sm flex items-center gap-1"
                  data-testid={`scan-${item.id}`}
                >
                  {scanningId === item.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <RefreshCw size={14} />
                  )}
                  Scanner
                </button>
                <button
                  onClick={async () => {
                    try {
                      const newTrack = !item.track_stock;
                      await axios.put(`${API}/watchlist/${item.id}/track-stock?track=${newTrack}`);
                      toast.success(newTrack ? "Suivi du stock active" : "Suivi du stock desactive");
                      fetchWatchlist();
                    } catch { toast.error("Erreur"); }
                  }}
                  className={`text-sm flex items-center gap-1 px-3 py-1.5 border-2 border-[#0A0A0A] font-bold uppercase transition-colors ${
                    item.track_stock ? "bg-[#00BCD4] text-white" : "bg-transparent hover:bg-[#00BCD4]/10"
                  }`}
                  data-testid={`track-stock-${item.id}`}
                >
                  <Package size={14} />
                  {item.track_stock ? "Stock suivi" : "Suivre stock"}
                </button>
                <button
                  onClick={() => handleRemove(item.id)}
                  className="neo-button-secondary text-sm flex items-center gap-1 text-red-500 hover:bg-red-50"
                  data-testid={`remove-${item.id}`}
                >
                  <Trash2 size={14} />
                  Retirer
                </button>
                {/* Price change indicator */}
                {item.price_change && (
                  <span className={`ml-auto text-xs font-bold flex items-center gap-1 ${item.price_change < 0 ? "text-[#00E676]" : "text-[#FF5722]"}`}>
                    {item.price_change < 0 ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
                    {item.price_change > 0 ? "+" : ""}{item.price_change}%
                  </span>
                )}
                {item.stock_status === "out_of_stock" && (
                  <span className="ml-auto text-xs font-bold text-[#FF5722] flex items-center gap-1 border border-[#FF5722]/50 px-2 py-0.5">
                    <Package size={12} /> Rupture
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="neo-card p-12 text-center" data-testid="watchlist-empty">
          <Eye className="mx-auto mb-4 text-muted-foreground" size={64} />
          <p className="text-xl font-bold text-muted-foreground">Votre watchlist est vide</p>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">
            Utilisez le bouton "Suivre" dans le Scanner ou les Opportunités pour ajouter des produits à surveiller
          </p>
        </div>
      )}
    </div>
  );
}
